import React, { useState, useEffect, useRef } from "react";
import { Bell, CheckCheck, Play, Sparkles, ExternalLink, Loader2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext.js";
import {
  NotificationItem,
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../services/notificationService.js";

export interface NotificationMenuProps {
  userId?: string;
  onNavigate?: (url: string) => void;
}

function formatRelativeTime(dateStr: string, lang: string): string {
  try {
    const date = new Date(dateStr);
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);

    if (diffSec < 60) {
      if (lang === "fr") return "à l'instant";
      if (lang === "ln") return "sik'oyo";
      if (lang === "sw") return "sasa hivi";
      return "just now";
    }

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      if (lang === "fr") return `il y a ${diffMin} min`;
      if (lang === "ln") return `eleki min ${diffMin}`;
      if (lang === "sw") return `dakika ${diffMin} zilizopita`;
      return `${diffMin}m ago`;
    }

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) {
      if (lang === "fr") return `il y a ${diffHours} heure${diffHours > 1 ? "s" : ""}`;
      if (lang === "ln") return `eleki ngonga ${diffHours}`;
      if (lang === "sw") return `saa ${diffHours} zilizopita`;
      return `${diffHours}h ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    if (lang === "fr") return `il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
    if (lang === "ln") return `eleki mikolo ${diffDays}`;
    if (lang === "sw") return `siku ${diffDays} zilizopita`;
    return `${diffDays}d ago`;
  } catch {
    return "";
  }
}

export const NotificationMenu: React.FC<NotificationMenuProps> = ({ userId, onNavigate }) => {
  const { t, language } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const data = await fetchNotifications(userId);
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // 30s poll
    return () => clearInterval(interval);
  }, [userId]);

  // Outside click handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAllRead = async () => {
    await markAllNotificationsAsRead(userId);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const handleItemClick = async (notif: NotificationItem) => {
    if (!notif.isRead) {
      markNotificationAsRead(notif.id, userId);
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setIsOpen(false);

    if (onNavigate) {
      onNavigate(notif.targetUrl);
    } else {
      window.location.href = notif.targetUrl;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Trigger Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) loadNotifications();
        }}
        className="relative p-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white transition-all shadow-sm focus:outline-none"
        title={t.notifications?.title || "Notifications"}
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-zinc-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white font-mono text-[10px] font-extrabold rounded-full flex items-center justify-center ring-2 ring-zinc-950 animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Interactive Dropdown Tray */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Tray Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/80 bg-zinc-900/60">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-bold text-white tracking-tight">
                {t.notifications?.title || "Notifications"}
              </h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-mono text-[10px] font-bold">
                  {unreadCount}
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-emerald-400 transition-colors font-medium"
                title={t.notifications?.markAllAsRead || "Mark all as read"}
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>{t.notifications?.markAllAsRead || "Mark all as read"}</span>
              </button>
            )}
          </div>

          {/* Tray Body / Feed */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-zinc-900/80">
            {isLoading && notifications.length === 0 ? (
              <div className="p-8 flex flex-col items-center justify-center text-center gap-2">
                <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                <span className="text-xs text-zinc-500 font-mono">Loading notifications...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 flex flex-col items-center justify-center text-center space-y-2">
                <div className="w-12 h-12 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500">
                  <Bell className="w-6 h-6 text-zinc-600" />
                </div>
                <p className="text-xs text-zinc-400 font-medium">
                  {t.notifications?.empty || "No new notifications"}
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleItemClick(notif)}
                  className={`flex items-start gap-3 p-3.5 hover:bg-zinc-900/80 transition-colors cursor-pointer group ${
                    !notif.isRead ? "bg-emerald-500/[0.04]" : ""
                  }`}
                >
                  {/* Thumbnail / Icon Badge */}
                  <div className="relative shrink-0 w-16 aspect-video rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
                    {notif.thumbnailUrl ? (
                      <img
                        src={notif.thumbnailUrl}
                        alt={notif.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-zinc-600">
                        <Play className="w-4 h-4 text-emerald-400 fill-emerald-400/20" />
                      </div>
                    )}
                    {!notif.isRead && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-zinc-950" />
                    )}
                  </div>

                  {/* Metadata & Message */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-bold text-white truncate group-hover:text-emerald-400 transition-colors">
                        {notif.title}
                      </p>
                      <span className="text-[10px] text-zinc-500 font-mono shrink-0">
                        {formatRelativeTime(notif.createdAt, language)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-300 line-clamp-2 leading-relaxed">
                      {notif.message}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
